import { useEffect, useState } from 'react';
import { initZoom, zoomSdk } from '../lib/zoom';

export interface ZoomContext {
  /**
   * 'inMeeting' | 'inMainClient' | … from the Zoom SDK, or 'browser' when
   * the app is running in an ordinary browser tab (no Zoom client at all).
   */
  runningContext: string;
  meetingId: string | null;
  meetingTopic: string | null;
  userId: string | null;
  userName: string | null;
}

type State =
  | { status: 'loading' }
  | { status: 'ready'; context: ZoomContext }
  | { status: 'error'; error: string };

/**
 * True when we're inside the Zoom client's embedded browser. Zoom stamps
 * its webviews with "ZoomApps" (and older builds with "ZoomWebKit") in the
 * user agent; a plain browser tab has neither, and calling the SDK there
 * would just hang/fail.
 */
export function isZoomClient(): boolean {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  return ua.includes('ZoomApps') || ua.includes('ZoomWebKit');
}

const BROWSER_CONTEXT: ZoomContext = {
  runningContext: 'browser',
  meetingId: null,
  meetingTopic: null,
  userId: null,
  userName: null,
};

/**
 * Reads the current Zoom meeting + user context from the Zoom Apps SDK.
 *
 * Render-mode switch hangs off `runningContext`:
 *   - 'inMeeting'  → the sidebar experience (briefing / live / agreement)
 *   - anything else ('inMainClient', 'browser', …) → the dashboard
 *
 * The running context comes from `config()`'s response — the standalone
 * getRunningContext call has a known main-client permission quirk — with a
 * fallback to the standalone call for older SDK responses.
 */
export function useZoomContext(): State {
  const [state, setState] = useState<State>(() =>
    isZoomClient() ? { status: 'loading' } : { status: 'ready', context: BROWSER_CONTEXT },
  );

  useEffect(() => {
    if (!isZoomClient()) return;
    let cancelled = false;

    const load = async () => {
      try {
        const config = await initZoom();
        let running =
          (config as { runningContext?: string }).runningContext ?? null;
        if (!running) {
          const res = await zoomSdk.getRunningContext();
          running = res.context;
        }

        let meetingId: string | null = null;
        let meetingTopic: string | null = null;
        if (running === 'inMeeting') {
          try {
            const meeting = await zoomSdk.getMeetingContext();
            meetingId = meeting.meetingID ?? null;
            meetingTopic = meeting.meetingTopic ?? null;
          } catch {
            // User hasn't granted meeting scope yet; UI will prompt via OAuth.
          }
        }

        let userId: string | null = null;
        let userName: string | null = null;
        try {
          const user = await zoomSdk.getUserContext();
          userId = user.participantUUID ?? null;
          userName = user.screenName ?? null;
        } catch {
          // Same — surfaces via OAuth prompt in the app shell.
        }

        if (cancelled) return;
        setState({
          status: 'ready',
          context: {
            runningContext: running,
            meetingId,
            meetingTopic,
            userId,
            userName,
          },
        });
      } catch (err) {
        if (cancelled) return;
        setState({
          status: 'error',
          error: err instanceof Error ? err.message : 'Failed to initialize Zoom SDK',
        });
      }
    };

    load();

    // Zoom reuses the SAME webview when an open app follows the user from
    // the main client into a meeting (and back). Without this listener the
    // context read at startup sticks, and the dashboard keeps rendering
    // inside the meeting panel instead of the sidebar.
    const refreshOnContextChange = async (evt: { runningContext?: string }) => {
      try {
        let running = evt?.runningContext ?? null;
        if (!running) {
          const res = await zoomSdk.getRunningContext();
          running = res.context;
        }

        let meetingId: string | null = null;
        let meetingTopic: string | null = null;
        if (running === 'inMeeting') {
          try {
            const meeting = await zoomSdk.getMeetingContext();
            meetingId = meeting.meetingID ?? null;
            meetingTopic = meeting.meetingTopic ?? null;
          } catch {
            // Meeting scope not granted yet; the shell prompts via OAuth.
          }
        }

        if (cancelled) return;
        setState((prev) => ({
          status: 'ready',
          context: {
            ...(prev.status === 'ready' ? prev.context : BROWSER_CONTEXT),
            runningContext: running!,
            meetingId,
            meetingTopic,
          },
        }));
      } catch (err) {
        console.error('[zoom] running-context refresh failed', err);
      }
    };

    initZoom()
      .then(() => zoomSdk.onRunningContextChange(refreshOnContextChange))
      .catch(() => {
        // config() failure already surfaced via the error state above.
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}
